/* eslint-disable guard-for-in */
import React, { useState } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import SearchIcon from '@material-ui/icons/Search';
import {
  useQueryParams,
  NumberParam,
  StringParam,
  withDefault,
  JsonParam,
} from 'use-query-params';
import { filterCaseInsensitive } from '../util/TableUtils';
import TableContainer from './CustomTableHelpers/TableContainer';

const TitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;

  h4,
  h5,
  h6 {
    margin-bottom: 0px;
  }

  .title-row-right {
    display: flex;
    align-items: center;
  }
`;

const container = css`
  .topItems {
    display: flex;
    justify-content: space-between;
  }

  .ReactTable {
    font-size: 0.95rem;
    border: none;
    color: #666;
    .-sort-desc {
      box-shadow: none !important;
      &:after {
        content: '▼';
        float: right;
        padding-left: 4px;
        font-size: 0.8rem;
      }
    }

    .-sort-asc {
      box-shadow: none !important;
      &:after {
        content: '▲';
        float: right;
        padding-left: 4px;
        font-size: 0.8rem;
      }
    }
  }

  .ReactTable .rt-table {
    overflow: visible;
  }

  .rt-tr {
    border: none !important;
    background: white;
    padding: 0.6rem 0.5rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.1);
    align-items: center;
  }

  .rt-tr-group {
    border: none !important;
    & :first-of-type {
      .rt-tr {
        border-radius: 4px 4px 0px 0px;
      }
    }

    & :last-child {
      .rt-tr {
        border-radius: 0px 0px 4px 4px;
      }
    }
  }
  .rt-td {
    border: none !important;
  }

  .rt-tbody {
    overflow: visible !important;
    min-height: 50px;
  }

  .rt-thead {
    border: none !important;
    box-shadow: none !important;
    overflow: visible !important;
    margin-bottom: 10px;
    .rt-tr {
      padding: 0px !important;
      border-radius: 4px;
    }
    .rt-th {
      height: 60px;
      align-items: center;
      justify-content: center;
      display: flex;
    }
  }

  .rt-resizable-header-content {
    font-weight: bold;
  }

  .pagination-bottom {
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.1);
    margin-top: 10px;
    border-radius: 4px;
  }

  .-pagination {
    border-top: none !important;
    border-radius: 4px;
  }
  .rt-noData {
    margin-top: 8px;
    background: transparent;
  }
`;

const searchContainer = css`
  display: flex;
  align-items: center;
  border: 1px solid rgba(0, 0, 0, 0.23);
  border-radius: 3px;
  margin-right: 10px;
  padding: 3px 5px;
  .rtUniversalSearch {
    color: #525252;
    box-sizing: border-box;
    border: none;
    background: #fff;
    padding: 5px 1px 5px 2px;
    font-size: inherit;
    border-radius: 3px;
    font-weight: normal;
    outline-width: 0;
  }
`;

export const CustomTable = ({
  actionables,
  tableHeader,
  hideHeader = false,
  containerStyle,
  filterable = true,
  data,
  columns,
  disableQueryParams,
  rowInfo,
  ...rest
}) => {
  const { FoundRows, TotalRows } = rowInfo || {};
  const [query, setQuery] = useQueryParams({
    page: NumberParam,
    rows: NumberParam,
    search: withDefault(StringParam, ''),
    sort: withDefault(JsonParam, []),
  });
  const [searchString, setSearchString] = useState(query.search);
  // const [isSorted, setIsSorted] = useState(false);
  // const [sort, setSort] = useState([]);

  // const handleSortChange = newSort => {
  //   // Ascending
  //   if (!isSorted) {
  //     setSort(newSort);
  //     setIsSorted(true);
  //     // None
  //   } else if (!newSort[0].desc) {
  //     setSort([]);
  //     setIsSorted(false);
  //     // Descending
  //   } else {
  //     setSort(newSort);
  //   }
  // };

  function filterByValue(array, string) {
    if (!string) return array;
    return array.filter(o =>
      Object.keys(o).some(k => {
        // if it's an ID remove it from filter consideration
        // TODO: remove otehr object propertiesfrom search options
        if (k.includes('Id')) {
          return false;
        }
        if (o[k] && o[k] !== undefined) {
          return String(o[k])
            .toLowerCase()
            .includes(string.toLowerCase());
        }
        return false;
      }),
    );
  }

  const changeSearchString = e => {
    const { value } = e.target;
    setSearchString(value);
    if (!disableQueryParams) {
      if (value === '') {
        setQuery({ page: 1, search: undefined });
      } else {
        setQuery({ page: 1, search: value });
      }
    }
  };

  const filteredData = filterByValue(data, searchString);

  return (
    <div css={container} style={containerStyle}>
      {!hideHeader && (
        <TitleRow>
          {tableHeader}
          <div className="title-row-right">
            {FoundRows < TotalRows && (
              <div
                style={{
                  float: 'left',
                  marginRight: 20,
                  fontSize: 15,
                  fontWeight: '600',
                }}
              >
                {FoundRows || 0} of {TotalRows || 0} records
              </div>
            )}
            {filterable && (
              <div css={searchContainer}>
                <SearchIcon />
                <input
                  className="rtUniversalSearch"
                  onChange={changeSearchString}
                  placeholder={
                    FoundRows >= 0
                      ? `Search ${FoundRows} records...`
                      : 'Search...'
                  }
                  value={searchString}
                />
              </div>
            )}

            {actionables}
          </div>
        </TitleRow>
      )}

      <TableContainer
        data={filteredData}
        columns={columns}
        sortable
        multiSort={false}
        // sorted={sort} // this.state.sort is initially []
        // onSortedChange={handleSortChange}
        defaultFilterMethod={filterCaseInsensitive}
        query={disableQueryParams ? {} : query}
        setQuery={disableQueryParams ? null : setQuery}
        {...rest}
      />
    </div>
  );
};

export const FilterPlaceholder = ({ filter, onChange, ...rest }) => (
  <input
    type="text"
    placeholder="Search..."
    style={{
      width: '100%',
    }}
    value={filter ? filter.value : ''}
    onChange={event => onChange(event.target.value)}
    {...rest}
  />
);
